<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import ScheduledMessagesAPI from './api';
import {
  WASAPI_SCHEDULE_MESSAGE,
  WASAPI_SCHEDULED_MESSAGES_CHANGED,
} from './constants';
import { formatScheduledAt } from './helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  inboxId: { type: [Number, String], default: null },
});

const { t, locale } = useI18n();
const items = ref([]);
const isLoading = ref(false);

const STATUS_STYLE = {
  pending: 'text-n-blue-11',
  processing: 'text-n-amber-11',
  sent: 'text-n-teal-11',
  failed: 'text-n-ruby-11',
  canceled: 'text-n-slate-10',
};

const fetch = async () => {
  if (!props.conversationId) return;
  isLoading.value = true;
  try {
    const { data } = await ScheduledMessagesAPI.list(props.conversationId);
    items.value = data;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetch);
watch(() => props.conversationId, fetch);
useEmitter(WASAPI_SCHEDULED_MESSAGES_CHANGED, id => {
  if (Number(id) === Number(props.conversationId)) fetch();
});

const pendingItems = computed(() =>
  items.value.filter(i => i.status === 'pending')
);
const pastItems = computed(() =>
  items.value.filter(i => i.status !== 'pending')
);

const openNew = () =>
  emitter.emit(WASAPI_SCHEDULE_MESSAGE, {
    conversationId: props.conversationId,
    inboxId: props.inboxId,
  });
const edit = item =>
  emitter.emit(WASAPI_SCHEDULE_MESSAGE, {
    conversationId: props.conversationId,
    inboxId: props.inboxId,
    scheduled: item,
  });

const cancel = async item => {
  try {
    await ScheduledMessagesAPI.cancel(props.conversationId, item.id);
    useAlert(t('WASAPI_SCHEDULED.CANCELED'));
    fetch();
  } catch {
    useAlert(t('WASAPI_SCHEDULED.SAVE_ERROR'));
  }
};

const label = item =>
  item.template_params
    ? t('WASAPI_SCHEDULED.TEMPLATE_LABEL', { name: item.template_params.name })
    : item.content;
</script>

<template>
  <div class="flex flex-col gap-2">
    <p v-if="!items.length && !isLoading" class="text-xs text-n-slate-11">
      {{ t('WASAPI_SCHEDULED.EMPTY') }}
    </p>
    <div
      v-for="item in pendingItems"
      :key="item.id"
      class="flex flex-col gap-1 p-2 rounded-lg outline outline-1 outline-n-weak"
    >
      <div
        class="flex items-center gap-1.5 text-xs"
        :class="STATUS_STYLE[item.status]"
      >
        <Icon icon="i-lucide-clock" class="size-3.5" />
        {{ formatScheduledAt(item.scheduled_at, locale) }}
      </div>
      <p class="text-sm text-n-slate-12 line-clamp-3">{{ label(item) }}</p>
      <div class="flex items-center gap-2">
        <Button
          :label="t('WASAPI_SCHEDULED.EDIT')"
          size="xs"
          variant="link"
          color="slate"
          @click="edit(item)"
        />
        <Button
          :label="t('WASAPI_SCHEDULED.CANCEL')"
          size="xs"
          variant="link"
          color="ruby"
          @click="cancel(item)"
        />
      </div>
    </div>
    <details v-if="pastItems.length" class="text-xs text-n-slate-11">
      <summary class="cursor-pointer">
        {{ t('WASAPI_SCHEDULED.HISTORY', { count: pastItems.length }) }}
      </summary>
      <ul class="flex flex-col gap-1 mt-1">
        <li v-for="item in pastItems" :key="item.id" class="flex flex-col">
          <span :class="STATUS_STYLE[item.status]">
            {{ t(`WASAPI_SCHEDULED.STATUS.${item.status.toUpperCase()}`) }} ·
            {{ formatScheduledAt(item.scheduled_at, locale) }}
          </span>
          <span class="truncate text-n-slate-12">{{ label(item) }}</span>
          <span v-if="item.error" class="text-n-ruby-11">{{ item.error }}</span>
        </li>
      </ul>
    </details>
    <Button
      :label="t('WASAPI_SCHEDULED.NEW')"
      icon="i-lucide-plus"
      size="xs"
      variant="faded"
      color="blue"
      class="self-start"
      @click="openNew"
    />
  </div>
</template>
